/**
 * Positionen von Hand (Plan API 2026-11-01/01, E19): Die Eingabe der Nutzerin wird zu Positionen und Summen der Rechnung.
 * Kein vorbelegter Steuersatz: Er ist ein Rechtswert und steht nur, wenn die Nutzerin ihn einträgt (`rechtswert-in-der-spec.md`).
 */
import type { InvoiceDraftItem } from '@/api/pdfExtractClient'

export interface ManualItem {
  description: string
  quantity: string
  unitPrice: string
  taxRate: string
  /** E24: Zeitraum der Position (BT-134/135), `YYYY-MM-DD`, freiwillig. */
  periodStart: string
  periodEnd: string
}

/** Beschreibung 500 wie `description` der Position im Eingabeschema von invoice-backend; Zahlen als Text, Grenze gegen Unfug. */
export const MANUAL_ITEM_LIMITS: Record<keyof ManualItem, number> = {
  description: 500,
  quantity: 20,
  unitPrice: 20,
  taxRate: 6,
  periodStart: 10,
  periodEnd: 10,
}

export const EMPTY_MANUAL_ITEM: ManualItem = {
  description: '',
  quantity: '',
  unitPrice: '',
  taxRate: '',
  periodStart: '',
  periodEnd: '',
}

export function isBlankManualItem(item: ManualItem): boolean {
  return Object.values(item).every((value) => value.trim() === '')
}

/** „1.280,50“ und „1280.50“ sind dieselbe Zahl; alles andere ist keine. */
export function parseDecimal(value: string): number | null {
  const compact = value.trim().replace(/\s+/g, '')
  const plain = compact.includes(',') ? compact.replace(/\./g, '').replace(',', '.') : compact
  if (!/^-?\d+(?:\.\d+)?$/.test(plain)) return null
  return Number(plain)
}

const cents = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

const asText = (n: number | null | undefined): string => (typeof n === 'number' ? String(n) : '')

/** Die erkannten Positionen als Startwert fürs Bearbeiten. Ohne Einzelpreis gilt der Netto-Betrag bei Menge 1. */
export function manualItemsFrom(items: readonly InvoiceDraftItem[]): ManualItem[] {
  return items.map((item) => ({
    description: item.description ?? '',
    quantity: asText(item.quantity ?? 1),
    unitPrice: asText(item.unitPrice ?? item.netAmount),
    taxRate: asText(item.taxRate),
    periodStart: item.servicePeriod?.start ?? '',
    periodEnd: item.servicePeriod?.end ?? '',
  }))
}

export interface ManualTotals {
  items: InvoiceDraftItem[]
  taxSummary: { taxRate: number; taxCategoryCode: string; netAmount: number; taxAmount: number }[]
  subtotal: number
  taxTotal: number
  total: number
}

/** Kategorie wie beim Auslesen (`assemble.ts`): Satz über 0 ist S, 0 ist Z. */
const categoryOf = (rate: number): string => (rate > 0 ? 'S' : 'Z')

/**
 * Die Summen der eingetragenen Zeilen. `null`, solange keine vollständige Zeile da ist oder eine Zeile eine Lücke hat —
 * leere Zeilen zählen nicht. Die Steuer je Satz wird wie in EN 16931 (BR-CO-17) aus der Netto-Summe des Satzes gerechnet.
 */
export function computeManualTotals(rows: readonly ManualItem[]): ManualTotals | null {
  const filled = rows.filter((row) => !isBlankManualItem(row))
  if (filled.length === 0) return null

  const items: InvoiceDraftItem[] = []
  const netByRate = new Map<number, number>()
  for (const row of filled) {
    const description = row.description.trim()
    const quantity = parseDecimal(row.quantity)
    const unitPrice = parseDecimal(row.unitPrice)
    const taxRate = parseDecimal(row.taxRate)
    if (
      !description ||
      quantity === null ||
      unitPrice === null ||
      taxRate === null ||
      taxRate < 0
    ) {
      return null
    }
    const netAmount = cents(quantity * unitPrice)
    const start = row.periodStart.trim()
    const end = row.periodEnd.trim()
    const taxAmount = cents((netAmount * taxRate) / 100)
    items.push({
      description,
      quantity,
      unit: 'C62',
      unitPrice,
      netAmount,
      taxRate,
      taxCategoryCode: categoryOf(taxRate),
      taxAmount,
      grossAmount: cents(netAmount + taxAmount),
      ...(start || end
        ? { servicePeriod: { ...(start ? { start } : {}), ...(end ? { end } : {}) } }
        : {}),
    })
    netByRate.set(taxRate, cents((netByRate.get(taxRate) ?? 0) + netAmount))
  }

  const taxSummary = [...netByRate].map(([taxRate, netAmount]) => ({
    taxRate,
    taxCategoryCode: categoryOf(taxRate),
    netAmount,
    taxAmount: cents((netAmount * taxRate) / 100),
  }))
  const subtotal = cents(taxSummary.reduce((sum, row) => sum + row.netAmount, 0))
  const taxTotal = cents(taxSummary.reduce((sum, row) => sum + row.taxAmount, 0))
  return { items, taxSummary, subtotal, taxTotal, total: cents(subtotal + taxTotal) }
}
